export const CardProductCode : {[x: string]: number} = Object.freeze({
    DreamCard       : 100921,
    PremierCard     : 100922,
    DirectCard      : 101007,
    WelcomeCard     : 103079,
    QuickCard       : 104560,
    MultiCurrency   : 101676,
    FamilyBlue      : 101687,
    FamilyPurple    : 101688
})

/**
 * 우편배송 전환 상품.
 * BC 가 본사를 거치지 않고 고객 주소로 직접 우편 발송하므로 수령방법을 고를 필요가 없다.
 * CardReceiveMethodSelect 에서 우편함(02) 하나만 노출한다.
 */
export const PostDeliveryCardProductCodeList : Array<number> = Object.freeze([
    CardProductCode.DreamCard,
    CardProductCode.PremierCard,
    CardProductCode.WelcomeCard
]) as Array<number>;

/**
 * 신규 네이티브 주소화면(브릿지 openReact_Web)이 도입된 최소 앱버전. (가정값 — 앱팀 확정 후 교체)
 * CardReceiveMethodSelect.clickNext 에서 분기:
 * - apiVersion >= 이 값  → 신규: CardReceiveMethodSelect 가 직접 openReact_Web("card/address") 호출 (별도 화면 없음)
 * - apiVersion <  이 값  → 기존: /apply/step6 원본 CardDeliverySelect
 */
export const NEW_ADDRESS_SCREEN_MIN_VERSION = 1250;
// TODO 버전 확정되면 변경 필요
export const NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION = 1300;

/**
 * KYC 인증요청 팝업을 네이티브가 처리하는 최소 앱버전.
 * - apiVersion >= 이 값  → showKycRequiredPopupWeb() 호출
 * - apiVersion <  이 값  → 기존 callAlertWeb() 안내
 */
export const KYC_REQUIRED_POPUP_MIN_VERSION = 1500;

/**
 * 카드 신청 가능 최소 앱버전. 이 값 미만이면 카드 신청 버튼 클릭 시 앱 업데이트 안내 시스템 얼럿 노출.
 * 적용: Main clickApply(퀵카드 제외)·clickReissue, ProductDetail clickApply, BenefitsTab clickApply(퀵카드 제외)·OnUpgradeCardClick, ProductTab clickApply
 */
export const APP_UPDATE_REQUIRED_MIN_VERSION = 1510;

export const CardProductCodeName : {[x: number]: string} = Object.freeze({
    100921  : "Dream Card",
    100922  : "Premier Card",
    101007  : "Direct Card",
    103079  : "Welcome Card",
    104560  : "Quick Card",
    101676  : "Multi Currency",
    101687  : "FAMILY BLUE",
    101688  : "FAMILY PURPLE"
});

export const CardProductTabName : {[x: number]: string} = Object.freeze({
    100921  : "Dream",
    100922  : "Premier",
    101007  : "Direct",
    103079  : "Welcome",
    104560  : "Quick",
    101676  : "Multi",
    101687  : "BLUE",
    101688  : "PURPLE"
});

export const QUICK_CARD_REGISTER_MIN_VERSION = 795;

/** 카드 리스트(APPL01) 카테고리 배지 — 다국어 대상 아님 (영어 고정) */
export type CardListCategory = 'insurance' | 'healthCare' | 'rewards' | 'life';

export const CategoryBadgeName : {[x in CardListCategory]: string} = Object.freeze({
    insurance   : "Insurance",
    healthCare  : "Health Care",
    rewards     : "Rewards",
    life        : "Life"
});

type CardListSummaryItem = {
    category : CardListCategory;
    /** 다국어 sub 코드 (APPL01.{listCode}.{subCode}) */
    subCode  : string;
    defaults : string;
    /** 번역하지 않는 앞 문구. 있으면 다국어 문구를 굵게 표시 */
    prefix?  : string;
};

type CardListSummaryInfo = {
    listCode        : string;
    /** 카테고리 아이콘 색상 (bk·nv·gn·gy). 파일명 ic_{카테고리}_{색상}_16.png */
    iconColor       : "bk" | "nv" | "gn" | "gy";
    items           : Array<CardListSummaryItem>;
    /** 연간 혜택 금액 (번역하지 않음, 굵게 표시) */
    benefitAmount   : string;
    /** 연간 혜택 문구 다국어 키 */
    benefitKey      : string;
    benefitDefaults : string;
    buttonKey       : string;
    buttonDefaults  : string;
};

/**
 * 카드 리스트(APPL01) 카테고리별 요약.
 * 여기 없는 상품은 기존 리스트 형태로 노출한다.
 */
export const CardListSummary : {[x: number]: CardListSummaryInfo} = Object.freeze<{[x: number]: CardListSummaryInfo}>({
    100922 : {
        listCode : "list01",
        iconColor : "bk",
        items : [
            { category: "insurance",  subCode: "pre01", defaults: "Up to 40,000,000 KRW" },
            { category: "healthCare", subCode: "pre02", defaults: "Up to 1,300,000 KRW" },
            { category: "rewards",    subCode: "pre03", defaults: "Up to 82,000 P / year" },
            { category: "life",       subCode: "pre04", defaults: "Transit Card", prefix: "ATM &" }
        ],
        benefitAmount : "310,000 KRW+",
        benefitKey : "APPL01.listExplan01",
        benefitDefaults : "Yearly Benefits",
        buttonKey : "APPL01.listButton03",
        buttonDefaults : "Get Card"
    },
    100921 : {
        listCode : "list02",
        iconColor : "nv",
        items : [
            { category: "insurance",  subCode: "dre01", defaults: "Up to 20,000,000 KRW" },
            { category: "healthCare", subCode: "dre02", defaults: "Up to 1,000,000 KRW" },
            { category: "rewards",    subCode: "dre03", defaults: "Up to 56,000 P / year" },
            { category: "life",       subCode: "dre04", defaults: "Transit Card", prefix: "ATM &" }
        ],
        benefitAmount : "182,000 KRW+",
        benefitKey : "APPL01.listExplan01",
        benefitDefaults : "Yearly Benefits",
        buttonKey : "APPL01.listButton03",
        buttonDefaults : "Get Card"
    },
    103079 : {
        listCode : "list03",
        iconColor : "gn",
        items : [
            { category: "healthCare", subCode: "wel01", defaults: "Up to 1,000,000 KRW" },
            { category: "rewards",    subCode: "wel02", defaults: "Up to 12,000 P / year" },
            { category: "life",       subCode: "wel03", defaults: "Transit Card", prefix: "ATM &" }
        ],
        benefitAmount : "19,000 KRW+",
        benefitKey : "APPL01.listExplan01",
        benefitDefaults : "Yearly Benefits",
        buttonKey : "APPL01.listButton03",
        buttonDefaults : "Get Card"
    },
    104560 : {
        listCode : "list04",
        iconColor : "gy",
        items : [
            { category: "healthCare", subCode: "qui01", defaults: "Up to 1,000,000 KRW" },
            { category: "rewards",    subCode: "qui02", defaults: "Up to 12,000 P / year" },
            { category: "life",       subCode: "qui03", defaults: "Transit Card", prefix: "ATM &" }
        ],
        benefitAmount : "19,000 KRW+",
        benefitKey : "APPL01.listExplan01",
        benefitDefaults : "Yearly Benefits",
        buttonKey : "APPL01.listButton04",
        buttonDefaults : "Register Card"
    }
});
